import "styles/tabSpecBanners.css"
import React, { useMemo, useRef, useState } from "react"
import { Alert, Button, Drawer, Dropdown, Input, Modal, Select } from "antd"
import type { MenuProps } from "antd"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { faEllipsis } from "@fortawesome/free-solid-svg-icons"
import DataQButton from "shared/ui/Buttons/DataQButton.component"
import { BannerColumn, compileBanner } from "../../compiler/banner"
import { shownNames } from "../../model/shownLabels"
import { useTabSpecStore, useTabSpecStoreApi } from "../../runtime/tabSpecStore"
import { findQuestion } from "../../types/catalogue"
import { Banner, isConditionGroup, newTabSpecId } from "../../types/tabSpecDocument"
import BannerGroupEditor from "./BannerGroupEditor.component"
import {
  addConditionGroup,
  addGroup,
  BannerChoice,
  bannerChoices,
  bannerNameRefusal,
  copyBanner,
  groupHeadings,
  moveGroup,
  nestInto,
  removeGroup,
  setGroupLabel,
  setPoints,
} from "./bannerEdits"

const DEFAULT_STAYS =
  "The default banner cannot be deleted. Make another banner the default first."
const LAST_STAYS = "A tab spec needs at least one banner."

/** What the name dialog is for. */
type Naming = { mode: "new" } | { mode: "rename"; bannerId: string }

/** The two groups the nest dialog has chosen, by id. */
interface Nesting {
  outer: string | null
  inner: string | null
  error: string | null
}

/** What deleting a banner does to the tables that use it, in words. */
const usesText = (count: number, defaultName: string): string =>
  count === 0
    ? "No table uses it."
    : count === 1
      ? `1 table uses it. It will use the default banner, ${defaultName}, instead.`
      : `${count} tables use it. They will use the default banner, ${defaultName}, instead.`

/**
 * The header the tables will have: the group headings over their columns,
 * then Total. Built from the compiled banner, so what is left out of the
 * banner is left out here too; each heading and column as the tables show
 * it (`groupDisplay`, `display`): answers, nets, ranges and conditions
 * alike.
 */
const HeaderPreview: React.FC<{ columns: BannerColumn[] }> = ({ columns }) => {
  if (columns.length === 0) return <p className="ts-banner-preview__empty">Total only</p>
  // The columns come group by group (by the engine's heading, which is
  // unique); each heading spans its own.
  const groups: { header: string; shown: string; columns: BannerColumn[] }[] = []
  columns.forEach((c) => {
    const last = groups[groups.length - 1]
    if (last && last.header === c.group) last.columns.push(c)
    else groups.push({ header: c.group, shown: c.groupDisplay, columns: [c] })
  })
  return (
    <div className="ts-banner-preview__scroll">
      <table className="ts-banner-preview" aria-label="Header preview">
        <thead>
          <tr>
            {groups.map((g) => (
              <th key={g.header} scope="colgroup" colSpan={g.columns.length}>
                {g.shown}
              </th>
            ))}
            <th scope="col" rowSpan={2}>
              Total
            </th>
          </tr>
          <tr>
            {columns.map((c) => (
              <th key={`${c.group}//${c.label}`} scope="col">
                {c.display}
              </th>
            ))}
          </tr>
        </thead>
      </table>
    </div>
  )
}

export interface BannerDrawerProps {
  open: boolean
  onClose: () => void
  /** Where the drawer renders; the tab spec's root, so it gets the --qc-* tokens. */
  getContainer?: () => HTMLElement
}

/**
 * The banners of the spec: the column groups of the tables. The list on the
 * left creates banners, and each banner's menu renames, copies, makes the
 * default or deletes it (the dashboards' item menu); the chosen banner is
 * edited on the right, group by group, over a preview of the header and
 * what the tables will leave out. A group is a question (each grid row and
 * loop iteration on its own), a number with ranges, or a group of
 * conditions; "Nest" makes a group of conditions of two groups. Every edit
 * writes the document at once.
 */
const BannerDrawer: React.FC<BannerDrawerProps> = ({ open, onClose, getContainer }) => {
  const store = useTabSpecStoreApi()
  const banners = useTabSpecStore((s) => s.document.banners)
  const defaultId = useTabSpecStore((s) => s.document.defaults.bannerId)
  const catalogue = useTabSpecStore((s) => s.catalogue)
  const labels = useTabSpecStore((s) => s.document.labels)
  const names = useMemo(() => shownNames(labels), [labels])
  const upsertBanner = useTabSpecStore((s) => s.upsertBanner)
  const removeBanner = useTabSpecStore((s) => s.removeBanner)
  const updateDefaults = useTabSpecStore((s) => s.updateDefaults)

  const [chosenId, setChosenId] = useState<string | null>(null)
  const [naming, setNaming] = useState<Naming | null>(null)
  const [nameDraft, setNameDraft] = useState("")
  const [nameError, setNameError] = useState<string | null>(null)
  const [deletingId, setDeletingId] = useState<string | null>(null)
  const [nesting, setNesting] = useState<Nesting | null>(null)
  // Counted only while a delete is asked about, so an edit of a table
  // renders nothing here.
  const deletingUses = useTabSpecStore((s) =>
    deletingId ? s.document.tables.filter((t) => t.bannerId === deletingId).length : 0
  )
  const bodyRef = useRef<HTMLDivElement>(null)
  // Dialogs render inside the spec, so they get the --qc-* tokens.
  const dialogContainer = (): HTMLElement =>
    bodyRef.current?.closest<HTMLElement>(".tabspec-root") ?? document.body

  // The default banner until another is chosen, and again when the chosen one goes.
  const chosen =
    banners.find((b) => b.id === chosenId) ??
    banners.find((b) => b.id === defaultId) ??
    banners[0]
  const defaultName = banners.find((b) => b.id === defaultId)?.name ?? ""
  const plan = useMemo(
    () => (chosen && catalogue ? compileBanner(chosen, catalogue, names) : null),
    [chosen, catalogue, names]
  )
  const headings = useMemo(
    () => (chosen ? groupHeadings(chosen, catalogue, names) : {}),
    [chosen, catalogue, names]
  )
  // Two groups: the questionnaire's questions, then the variables of the
  // prepared data; a group with nothing in it is left out. Each grid row and
  // loop iteration is a choice of its own. The value is the choice's place
  // in the list, since a label alone does not say which. Each choice as the
  // tables show it, its labels as the tooltip; a search finds both.
  const choices = useMemo(() => (catalogue ? bannerChoices(catalogue, names) : []), [catalogue, names])
  const questionOptions = useMemo(() => {
    const options = choices.map((c: BannerChoice, i) => ({
      data: c.source === "data",
      value: String(i),
      label: c.shown,
      title: c.title,
      search: c.shown === c.title ? c.title : `${c.shown}\n${c.title}`,
    }))
    return [
      { label: "Questionnaire", options: options.filter((o) => !o.data) },
      { label: "From the data", options: options.filter((o) => o.data) },
    ].filter((g) => g.options.length > 0)
  }, [choices])
  /** Adds the choice a value of the list names: its place in `choices`. */
  const addChosen = (bannerId: string, value: string): void => {
    const c = choices[Number(value)]
    if (c) edit(bannerId, (b) => addGroup(b, c.variable, undefined, c.source, c.instance))
  }
  const deleting = banners.find((b) => b.id === deletingId) ?? null

  /**
   * Edits a banner as the store holds it now, not as it was rendered.
   * Returns the refusal of the edit, or null once it is written.
   */
  const edit = (bannerId: string, fn: (b: Banner) => Banner | string): string | null => {
    const banner = store.getState().document.banners.find((b) => b.id === bannerId)
    if (!banner) return null
    const next = fn(banner)
    if (typeof next === "string") return next
    upsertBanner(next)
    return null
  }

  const openNaming = (next: Naming, name: string): void => {
    setNameDraft(name)
    setNameError(null)
    setNaming(next)
  }
  const confirmName = (): void => {
    const name = nameDraft.trim()
    if (!naming || !name) return
    const refusal = bannerNameRefusal(
      store.getState().document.banners,
      name,
      naming.mode === "rename" ? naming.bannerId : null
    )
    if (refusal) {
      setNameError(refusal)
      return
    }
    if (naming.mode === "new") {
      const banner: Banner = { id: newTabSpecId("banner"), name, groups: [] }
      upsertBanner(banner)
      setChosenId(banner.id)
    } else {
      edit(naming.bannerId, (b) => ({ ...b, name }))
    }
    setNaming(null)
  }
  const duplicate = (banner: Banner): void => {
    const copy = copyBanner(banner, store.getState().document.banners)
    upsertBanner(copy)
    setChosenId(copy.id)
  }
  const confirmDelete = (): void => {
    if (deletingId) removeBanner(deletingId)
    setDeletingId(null)
  }
  /** Appends the group of conditions of the two groups chosen; a refusal stays in the dialog. */
  const confirmNest = (bannerId: string): void => {
    if (!nesting || !catalogue) return
    // A group not chosen yet is refused as one group twice is.
    const error = edit(bannerId, (b) =>
      nestInto(b, nesting.outer ?? "", nesting.inner ?? "", catalogue, names)
    )
    if (error) setNesting({ ...nesting, error })
    else setNesting(null)
  }
  const deleteReason = (b: Banner): string | null =>
    b.id === defaultId ? DEFAULT_STAYS : banners.length <= 1 ? LAST_STAYS : null
  /** A banner's menu; Delete is off, with the reason as its title, for the one that must stay. */
  const menuOf = (b: Banner): MenuProps["items"] => {
    const reason = deleteReason(b)
    return [
      {
        key: "rename",
        label: "Rename",
        onClick: () => openNaming({ mode: "rename", bannerId: b.id }, b.name),
      },
      { key: "duplicate", label: "Duplicate", onClick: () => duplicate(b) },
      ...(b.id !== defaultId
        ? [
            {
              key: "default",
              label: "Make default",
              onClick: () => updateDefaults({ bannerId: b.id }),
            },
          ]
        : []),
      { type: "divider" },
      {
        key: "delete",
        label: "Delete",
        danger: true,
        disabled: reason !== null,
        title: reason ?? undefined,
        onClick: () => setDeletingId(b.id),
      },
    ]
  }

  return (
    <Drawer
      open={open}
      onClose={onClose}
      getContainer={getContainer}
      rootClassName="ts-drawer"
      placement="right"
      width={720}
      title="Banners"
    >
      <div ref={bodyRef} className="ts-banners">
        <div className="ts-banners__side">
          <ul className="ts-banners__list" aria-label="Banners">
            {banners.map((b) => {
              const isChosen = b.id === chosen?.id
              return (
                <li
                  key={b.id}
                  className={`ts-banners__item${isChosen ? " ts-banners__item--chosen" : ""}`}
                >
                  <div className="ts-banners__name">
                    <button
                      type="button"
                      className="ts-banners__pick"
                      aria-current={isChosen ? "true" : undefined}
                      onClick={() => setChosenId(b.id)}
                    >
                      {b.name}
                    </button>
                    {b.id === defaultId && (
                      <span className="ts-chip ts-banners__default">Default</span>
                    )}
                    <Dropdown trigger={["click"]} menu={{ items: menuOf(b) }}>
                      <Button
                        type="text"
                        size="small"
                        className="ts-banners__menu"
                        aria-label={`More actions for ${b.name}`}
                        icon={<FontAwesomeIcon icon={faEllipsis} />}
                      />
                    </Dropdown>
                  </div>
                </li>
              )
            })}
          </ul>
          <Button size="small" onClick={() => openNaming({ mode: "new" }, "")}>
            New banner
          </Button>
        </div>

        {chosen && (
          <section className="ts-banners__detail" aria-label={`Banner ${chosen.name}`}>
            <h3 className="ts-field__label">{chosen.name}</h3>
            <Select
              showSearch
              size="small"
              className="ts-field__select"
              aria-label="Add a question"
              placeholder="Add a question"
              value={null}
              options={questionOptions}
              optionFilterProp="search"
              notFoundContent="No question matches"
              disabled={!catalogue}
              onChange={(value: string) => addChosen(chosen.id, value)}
            />
            <div className="ts-banner-group__actions">
              <Button size="small" onClick={() => edit(chosen.id, (b) => addConditionGroup(b))}>
                Add a group of conditions
              </Button>
              <Button
                size="small"
                disabled={!catalogue || chosen.groups.length < 2}
                onClick={() => setNesting({ outer: null, inner: null, error: null })}
              >
                Nest…
              </Button>
            </div>
            {chosen.groups.length === 0 && (
              <p className="ts-hint">No groups: the tables show the Total column only.</p>
            )}
            {chosen.groups.map((g, i) => (
              <BannerGroupEditor
                key={g.id}
                group={g}
                question={
                  catalogue && !isConditionGroup(g) ? findQuestion(catalogue, g.variable, g.source) : null
                }
                catalogue={catalogue}
                heading={headings[g.id] ?? g.variable}
                names={names}
                onLabel={(label) => edit(chosen.id, (b) => setGroupLabel(b, g.id, label))}
                onPoints={(points) => edit(chosen.id, (b) => setPoints(b, g.id, points))}
                onMoveUp={
                  i > 0 ? () => edit(chosen.id, (b) => moveGroup(b, g.id, i - 1)) : undefined
                }
                onMoveDown={
                  i < chosen.groups.length - 1
                    ? () => edit(chosen.id, (b) => moveGroup(b, g.id, i + 1))
                    : undefined
                }
                onRemove={() => edit(chosen.id, (b) => removeGroup(b, g.id))}
                getContainer={dialogContainer}
              />
            ))}
            <section className="ts-field" aria-label="Header">
              <h4 className="ts-field__label">Header</h4>
              {plan ? (
                <HeaderPreview columns={plan.columns} />
              ) : (
                <p className="ts-hint">The header shows once the questionnaire is read.</p>
              )}
            </section>
            {plan && plan.problems.length > 0 && (
              <Alert
                type="warning"
                showIcon
                message="Left out of this banner"
                description={
                  <ul className="ts-banners__problems">
                    {plan.problems.map((p, i) => (
                      <li key={i}>{p}</li>
                    ))}
                  </ul>
                }
              />
            )}
          </section>
        )}
      </div>

      <Modal
        open={naming !== null}
        title={naming?.mode === "rename" ? "Rename banner" : "New banner"}
        okText={naming?.mode === "rename" ? "Rename" : "Create"}
        onOk={confirmName}
        onCancel={() => setNaming(null)}
        okButtonProps={{ disabled: !nameDraft.trim() }}
        getContainer={dialogContainer}
        destroyOnHidden
      >
        <div className="ts-name-modal">
          <Input
            aria-label="Banner name"
            placeholder="Banner name"
            value={nameDraft}
            status={nameError ? "error" : undefined}
            onChange={(e) => {
              setNameDraft(e.target.value)
              setNameError(null)
            }}
            onPressEnter={confirmName}
          />
          {nameError && (
            <p className="ts-name-modal__error" role="alert">
              {nameError}
            </p>
          )}
        </div>
      </Modal>
      <Modal
        open={nesting !== null && !!chosen}
        title="Nest two groups"
        okText="Nest"
        onOk={() => chosen && confirmNest(chosen.id)}
        onCancel={() => setNesting(null)}
        getContainer={dialogContainer}
        destroyOnHidden
      >
        <div className="ts-name-modal">
          <p className="ts-hint">
            A new group gets a column for every column of the first group with every column of
            the second. Both groups stay in the banner.
          </p>
          {(["outer", "inner"] as const).map((side) => (
            <Select
              key={side}
              size="small"
              aria-label={side === "outer" ? "First group" : "Second group"}
              placeholder={side === "outer" ? "First group" : "Second group"}
              value={nesting?.[side] ?? null}
              options={(chosen?.groups ?? []).map((g) => ({
                value: g.id,
                label: headings[g.id] ?? g.variable,
                title: headings[g.id] ?? g.variable,
              }))}
              onChange={(id: string) => nesting && setNesting({ ...nesting, [side]: id, error: null })}
            />
          ))}
          {nesting?.error && (
            <p className="ts-name-modal__error" role="alert">
              {nesting.error}
            </p>
          )}
        </div>
      </Modal>
      {/* A confirmation in the dashboards' words and buttons: the way back
          first, the destructive action last. */}
      <Modal
        open={deleting !== null}
        title="Delete banner"
        onCancel={() => setDeletingId(null)}
        getContainer={dialogContainer}
        destroyOnHidden
        footer={[
          <DataQButton key="cancel" type="secondary" onClick={() => setDeletingId(null)}>
            Cancel
          </DataQButton>,
          <DataQButton key="delete" type="danger" onClick={confirmDelete}>
            Delete
          </DataQButton>,
        ]}
      >
        <p>
          {deleting ? `Delete "${deleting.name}"? ` : ""}
          {usesText(deletingUses, defaultName)}
        </p>
      </Modal>
    </Drawer>
  )
}

export default BannerDrawer
